'use client';

import type { ReactNode, RefObject } from 'react';
import { Spinner } from './ui/Spinner';
import { ErrorMessage } from './ErrorMessage';
import { EmptyState } from './ui/EmptyState';
import { ItemGridSkeleton } from './ItemGridSkeleton';
import { VirtualGrid } from './VirtualGrid';
import { ITEM_CARD_HEIGHT, ITEM_GRID_GAP, ITEM_GRID_MIN_COLUMN } from './ItemListItem';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

interface InfiniteItemGridProps<T> {
  items: T[];
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  renderItem: (item: T, index: number) => ReactNode;
  emptyTitle: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  errorText: string;
}

export function InfiniteItemGrid<T>({
  items,
  isLoading,
  isError,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  refetch,
  scrollRef,
  renderItem,
  emptyTitle,
  emptyDescription,
  emptyAction,
  errorText,
}: InfiniteItemGridProps<T>): ReactNode {
  useInfiniteScroll({
    hasMore: hasNextPage,
    loading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
    threshold: 200,
    scrollRef,
  });

  if (isLoading && items.length === 0) {
    return <ItemGridSkeleton count={8} />;
  }
  if (isError) {
    return <ErrorMessage error={errorText} onRetry={refetch} />;
  }
  if (items.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }

  return (
    <VirtualGrid
      items={items}
      scrollRef={scrollRef}
      rowHeight={ITEM_CARD_HEIGHT}
      gap={ITEM_GRID_GAP}
      minColumnWidth={ITEM_GRID_MIN_COLUMN}
      className="pt-2"
      renderItem={renderItem}
      footer={
        isFetchingNextPage ? (
          <div className="flex justify-center p-5">
            <Spinner size="s" />
          </div>
        ) : null
      }
    />
  );
}
